// 超清放大
import type { LayoutReferState } from "@/types/image";
import { useEventTracking } from "@/hooks/useEventTracking";
import { getClientId } from "@/hooks/useGenerate";
import { convertDownloadUrl, convertToJpg, getModuleLabelById } from "@xmiles/holopix-helper";
import { RenderAspectRatios, renderModeOptions } from "../options";
import { getModelEventTrackType, mapModelSource } from '../index';
import { startModuleGenerate } from "./index";
import type { UniversalRenderingModelItem } from "@/components/Model/service";
import { uploadFilePromise } from "@/utils/aliyunUpload";
import { registerUploadedAsset } from "@/hooks/useAssetUpload";

export const module = ModuleId.Render;

// 生图参数
export const renderParams = {
  module,
  renderMode: 0, // 渲染模式：0-单张渲染，1-批量渲染
  batchArtworkName: "", // 批量渲染模式下的作品名称（与单张模式独立维护）
  quality: RenderAspectRatios.Fast, // 尺寸质量：1K / 2K / 4K
  layoutRefer: null as null | LayoutReferState, // 原始稿件
  styleId: null as null | number, // 风格id
  selectStyle: null as null | UniversalRenderingModelItem, // 风格详情
  redrawWeight: 3, // 细节权重
  colorSettings: 3, // 色彩设置（1.色彩保持，2.色彩均衡，3.色彩变化）
  stylingSettings: 1, // 造型设置（1.造型保持，2.造型均衡，3.造型变化）
  /** 提示词控制开关（VIP）；与 selectStyle.prompt 无关 */
  openPromptControl: false,
  /** 提示词控制文案（顶层字段，勿与 selectStyle.prompt 混用） */
  promptControl: "",
}

import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { filteringInvalidFields } from "@/utils";
import { ModuleId } from "@xmiles/holopix-types";

/** 内部 type(0/1/2) -> API styleType(3/4/5) */
export const STYLE_TYPE_API_MAP: Record<number, number> = { 0: 3, 1: 4, 2: 5 };
/** 内部 type(0/1/2) -> API loraType 默认值 */
const LORA_TYPE_DEFAULT_MAP: Record<number, string> = { 0: "Klein", 1: "KleinFlux", 2: "KleinFlux2" };
/** loraType -> 内部 type 反向映射（用于兼容 type 字段缺失/不正确的场景） */
const LORA_TYPE_DEFAULT_MAP_REVERSE: Record<string, number> = { Klein: 0, KleinFlux: 1, KleinFlux2: 2 };

/**
 * 根据选中风格构建生图所需的 loraList
 *
 * 构建优先级：
 * 1. SVIP 定制（type=2）：使用风格记录自身的 id 作为 modelId
 * 2. 风格模板自带 loraList：将 id -> modelId、weight -> strength
 * 3. modelPublishList 数据源的 verList：使用选定版本的 modelId
 */
export function buildLoraList(selectStyle: UniversalRenderingModelItem | null): { modelId: number; strength?: number }[] {
  if (!selectStyle) return [];

  const isSvipCustom = Number(selectStyle.type) === 2;
  if (isSvipCustom) {
    return [{ modelId: selectStyle.selectedVersionId ?? selectStyle.id }];
  }

  if (selectStyle.loraList?.length) {
    // model/detail 返回的 LoraItem 只有 loraName/clipStrength/defaultStrength，
    // 没有 id 字段；此时应跳过本分支，走 selectedVersionId 兜底
    const validItems = selectStyle.loraList.filter(item => item.id != null);
    if (validItems.length) {
      return validItems.map(item => ({
        modelId: item.id,
        strength: item.weight,
      }));
    }
  }

  const versionModelId = selectStyle.selectedVersionId ?? selectStyle.verList?.[0]?.modelId;
  if (versionModelId != null) return [{ modelId: versionModelId }];

  return [{ modelId: selectStyle.id }];
}

async function convertLayoutReferToJpg(layoutRefer: LayoutReferState | null, artworkId: number): Promise<LayoutReferState | null> {
  if (!layoutRefer?.image) return layoutRefer;

  // 检查是否为 JPG 格式，是则跳过
  const lowerUrl = layoutRefer.image.toLowerCase();
  if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg')) {
    return layoutRefer;
  }

  try {
    // 先将 CDN URL 转为直连 OSS 加速域名，确保 x-oss-process 参数可靠处理
    const directUrl = convertDownloadUrl(layoutRefer.image);
    const jpgUrl = convertToJpg(directUrl);
    // 下载 JPG 图片并转为 File
    const response = await fetch(jpgUrl);
    // 检查 OSS 处理是否成功，避免将空/错误响应上传为空白图片
    if (!response.ok) {
      throw new Error(`OSS 转 JPG 失败: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const jpgFile = new File([blob], 'converted.jpg', { type: 'image/jpeg' });
    // 上传并注册新资源，获取新的 layoutReferId
    const ossUrl = await uploadFilePromise(jpgFile, { namescape: 'render-jpg' });
    const record = await registerUploadedAsset(jpgFile, ossUrl, 4, artworkId);
    if (record) {
      return {
        id: record.id ?? 0,
        image: record.image ?? ossUrl,
        width: record.width ?? layoutRefer.width,
        height: record.height ?? layoutRefer.height,
      };
    }
  } catch (err) {
    console.error('图片转 JPG 失败，使用原始图片:', err);
  }
  return layoutRefer;
}

function getRenderGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();


  const { layoutRefer, styleId, selectStyle, aspectRatios, quality, redrawWeight, colorSettings, stylingSettings, openPromptControl, promptControl } =
    artworkStore.render.params;

  // 推导内部 type：优先用 type 字段，其次根据 loraType 回退（兼容 type 未正确设置的场景）
  let internalType = Number(selectStyle?.type ?? 0);
  if (internalType === 0 && selectStyle?.loraType) {
    internalType = LORA_TYPE_DEFAULT_MAP_REVERSE[selectStyle.loraType] ?? 0;
  }
  const isAdvancedStyle = internalType === 1;
  const resolvedStylingSettings = isAdvancedStyle ? (stylingSettings ?? 1) : undefined;

  const apiStyleType = STYLE_TYPE_API_MAP[internalType] ?? 3;
  const apiLoraType = selectStyle?.loraType || LORA_TYPE_DEFAULT_MAP[internalType] || "Klein";
  const loraList = buildLoraList(selectStyle);

  let params = {
    module,
    source: 1,
    userId: userStore.userInfo.id,
    artworkId: artworkStore.artworkState.id,
    uniqueId: userStore.userInfo.uniqueId,
    styleId,
    styleType: apiStyleType,
    loraType: apiLoraType,
    selectStyle,
    layoutRefer,
    layoutReferId: layoutRefer?.id,
    originImgUrl: layoutRefer?.image,
    redrawWeight,
    colorSettings,
    stylingSettings: resolvedStylingSettings,
    aspectRatios,
    loraList,
    batchSize: 1,
    quality,
    openPromptControl: Boolean(openPromptControl),
    promptControl: openPromptControl ? String(promptControl ?? "").trim() : "",
  } as any;

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  if (!isAdvancedStyle) {
    delete params.stylingSettings;
  }
  return params;
}

// 开启模块生图
export async function startRenderGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const params = oldParams ?? getRenderGenerateParam();
  const clientId = oldParams?.apiClientId || getClientId();
  params.apiClientId = clientId;

  // 将 layoutRefer 图片转为 JPG 并重新注册获取新的 layoutReferId
  if (params.layoutRefer && !oldParams) {
    const jpgLayoutRefer = await convertLayoutReferToJpg(params.layoutRefer, artworkStore.artworkState.id);
    if (jpgLayoutRefer) {
      params.layoutRefer = jpgLayoutRefer;
      params.layoutReferId = jpgLayoutRefer.id;
      params.originImgUrl = jpgLayoutRefer.image;
      // 同步更新 store 中的 layoutRefer，保持一致性
      artworkStore.render.params.layoutRefer = jpgLayoutRefer;
    }
  }

  startModuleGenerate(module, {
    getModuleParams: () => {
      return oldParams || params;
    },
    onStartTrack: (clientId) => {
      reportStartEventTracking(clientId);
    },
    onFinishTrack: (data) => {
     userStore.queryUserRights();
      reportFinishEventTracking(data);
    },
    onClientIdGenerated: options?.onClientIdGenerated,
    onClientIdCleared: options?.onClientIdCleared
  });

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    const eventValue: any = {
      client_id: clientId,
      source: "直接生成",
      module: getModuleLabelById(module) as string,
      artwork_id: artworkStore.artworkState.id.toString(),
      batchSize: params.batchSize,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      model_source: mapModelSource(getModelEventTrackType(artworkStore.artworkState.modelList || [])),
      model_id: String((artworkStore.artworkState.modelList || []).map((item: any) => item.id).join(',')),
      zoom_mode: params.superImgType === "person" ? "标准" : "动漫",
      style_name: params.selectStyle.name,
      resolution: params.quality,
      detail_int: params.redrawWeight
    };

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }

  function reportFinishEventTracking({ result = 0 as 0 | 1, imageIds = "", queue_time_number = 0, loading_time_number = 0 } = {}) {
    useEventTracking({
      event: "artwork_status_event",
      eventValue: {
        module: getModuleLabelById(module) as string,
        client_id: clientId,
        source: "直接生成",
        img_id: imageIds,
        result,
        queue_time_number,
        loading_time_number,
        is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
        zoom_mode: params.superImgType === "person" ? "标准" : "动漫",
        style_name: params.selectStyle.name,
        resolution: params.quality,
        detail_int: params.redrawWeight
      }
    });
  }
}
